import { Button, IconButton, TextTitle } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, useEffect, useRef, useState } from 'react';

import { useProStudioViewerRole } from '../../hooks';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { agentSettingsStore } from '../../stores/agent-settings-store';
import { useAgentExtras } from './agent-extras';
import { tableStore } from '../../stores/table-store';
import { buildCanvasEdges } from '../canvas-view/canvas-edges-model';
import { buildCanvasAgentNodes } from '../canvas-view/canvas-view-model';
import {
  KNOWLEDGE_CHOICES,
  type KnowledgeSettings,
  SETTING_LABEL,
  SUPERVISOR_CHOICES,
  type SupervisorSettings,
  type AgentKey,
} from './agent-settings-model';
import { saveParameterInput, useAgentSettings } from './use-agent-settings';
import { saveExtrasBeforeSettings } from './agent-settings-write';
import { findModelNode } from './agent-prompt';
import {
  agentAiSteps,
  planRouting,
  supervisorRouting,
  type ModelRow,
} from './agent-models';
import { AgentModelSection, ModelSaved } from './agent-model-section';
import type { AgentIdentity } from './agent-identity';
import { AgentPromptEditor } from './agent-prompt-editor';
import { readStepModel, withStepModel } from '../../utils/gateway-models';
import { LangfusePromptCards } from './langfuse-prompt-cards';

import './agent-settings-panel.scss';

/**
 * Agent settings (Bobcat prototype): what the Supervisor and the Knowledge
 * Agent do, in plain choices. The prompts are written from these
 * (`agent-settings-model.ts`). Model picks each AI step's gateway model
 * (`agent-model-section.tsx`).
 */

const AGENT_TITLE: Record<AgentKey, string> = {
  supervisor: 'Supervisor Agent',
  knowledge: 'Knowledge Agent',
};

type Draft = {
  supervisor: SupervisorSettings;
  knowledge: KnowledgeSettings;
  /** Each agent's prompt, without the settings block; null with no node. */
  prompts: Record<AgentKey, string | null>;
  /** Each gateway AI step's model, by row id. */
  models: Record<string, string>;
  /** The Supervisor routes with OpenAI Decisions; null without a classifier. */
  decisions: boolean | null;
  /** Who the agents speak for (`agent-identity.ts`). */
  identity: AgentIdentity;
};

const modelNode = (agent: AgentKey) => findModelNode(tableStore.rows, agent);

const Section: FC<{
  title: string;
  sub?: string;
  children: React.ReactNode;
}> = ({ title, sub, children }) => (
  <section className="c-agent-settings__sec">
    <h3 className="c-agent-settings__sec-title">{title}</h3>
    {sub && <p className="c-agent-settings__sec-sub">{sub}</p>}
    {children}
  </section>
);

/**
 * `embedded`: inside the AI dock's Agents tab, which has its own header and
 * close — so no title or close of its own, and it fills the dock.
 */
export const AgentSettingsPanel: FC<{ embedded?: boolean }> = observer(
  ({ embedded = false }) => {
    const isViewer = useProStudioViewerRole();
    const extras = useAgentExtras(isViewer);
    const settings = useAgentSettings();
    // The use-case agents the canvas draws, and the intents that route to each.
    const rows = tableStore.getAllSortedRows();
    const edges = buildCanvasEdges(rows);
    const routes = buildCanvasAgentNodes(rows)
      .filter((a) => a.category !== 'core')
      .map((a) => ({
        id: a.id,
        title: a.title,
        intents: [
          ...new Set(
            edges
              .filter(
                (e) => e.target === a.id && e.kind === 'intent' && e.label
              )
              .map((e) => e.label as string)
          ),
        ],
      }));
    const { tab, flash } = agentSettingsStore;

    const modelRows = rows as unknown as ModelRow[];
    const aiSteps = {
      supervisor: agentAiSteps(modelRows, 'supervisor'),
      knowledge: agentAiSteps(modelRows, 'knowledge'),
    };
    const routing = supervisorRouting(modelRows);
    const loaded: Draft = {
      supervisor: settings.supervisor,
      knowledge: settings.knowledge,
      prompts: settings.prompts,
      models: Object.fromEntries(
        [...aiSteps.supervisor, ...aiSteps.knowledge].map((r) => [
          r.id,
          readStepModel(r.parameterInput),
        ])
      ),
      decisions: routing?.decisions ?? null,
      identity: settings.identity,
    };
    const loadedKey = JSON.stringify(loaded);
    const [draft, setDraft] = useState<Draft>(loaded);
    // A save, or a change the assistant applied, replaces what was loaded.
    useEffect(() => {
      setDraft(JSON.parse(loadedKey) as Draft);
    }, [loadedKey]);
    const settingsDirty =
      JSON.stringify({ ...draft, models: 0, decisions: 0, identity: 0 }) !==
      JSON.stringify({ ...loaded, models: 0, decisions: 0, identity: 0 });
    const identityDirty =
      JSON.stringify(draft.identity) !== JSON.stringify(loaded.identity);
    const modelsDirty =
      JSON.stringify(draft.models) !== JSON.stringify(loaded.models) ||
      draft.decisions !== loaded.decisions;
    const dirty = settingsDirty || modelsDirty || identityDirty || extras.dirty;
    // A model or routing change was just saved, until the next edit.
    const [modelSaved, setModelSaved] = useState(false);
    const draftVersion = useProStudioCtxSelector(
      (ctx) => ctx.draftVersion ?? null
    );
    const saving = settings.saving || extras.saving;
    const saveAll = async () => {
      // Tasks and documents first: the text saves after it read the version
      // it leaves (`agent-settings-write.ts`), where the other order put them
      // back as this form loaded them.
      await saveExtrasBeforeSettings({
        extras: extras.dirty ? extras.save : undefined,
        settings:
          identityDirty || settingsDirty
            ? async () => {
                if (identityDirty) await settings.saveIdentity(draft.identity);
                // Only the agent that changed: saving the other would add its
                // default block to text the designer never touched.
                if (settingsDirty) {
                  await settings.save({
                    ...(JSON.stringify(draft.supervisor) !==
                    JSON.stringify(loaded.supervisor)
                      ? { supervisor: draft.supervisor }
                      : {}),
                    ...(JSON.stringify(draft.knowledge) !==
                    JSON.stringify(loaded.knowledge)
                      ? { knowledge: draft.knowledge }
                      : {}),
                    prompts: Object.fromEntries(
                      (['supervisor', 'knowledge'] as const)
                        .filter(
                          (a) =>
                            draft.prompts[a] !== null &&
                            draft.prompts[a] !== loaded.prompts[a]
                        )
                        .map((a) => [a, draft.prompts[a] as string])
                    ),
                  });
                }
              }
            : undefined,
      });
      if (modelsDirty) {
        Object.entries(draft.models).forEach(([rowId, model]) => {
          if (loaded.models[rowId] === model) return;
          const row = modelRows.find((r) => r.id === rowId);
          const next = row && withStepModel(row.parameterInput, model);
          if (next) saveParameterInput(rowId, next);
        });
        if (routing && draft.decisions !== null) {
          if (draft.decisions !== loaded.decisions) {
            planRouting(modelRows, routing, draft.decisions).forEach((u) =>
              tableStore.updateRow({
                rowId: u.rowId,
                updates: u.updates as never,
                saveImmediately: true,
              })
            );
          }
        }
        setModelSaved(true);
      }
    };

    // Point at what just changed, once.
    const body = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!flash.length) return undefined;
      const els = flash
        .map((key) => body.current?.querySelector(`[data-setting="${key}"]`))
        .filter((el): el is Element => !!el);
      els.forEach((el) => {
        el.classList.remove('is-changed');
        void (el as HTMLElement).offsetWidth;
        el.classList.add('is-changed');
      });
      els[0]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const done = setTimeout(() => agentSettingsStore.clearFlash(), 2600);
      return () => clearTimeout(done);
    }, [flash, tab]);

    const set = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A],
      value: Draft[A][keyof Draft[A]]
    ) =>
      setDraft((prev) => ({
        ...prev,
        [agent]: { ...prev[agent], [key]: value },
      }));

    const choice = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A] & string,
      options: readonly string[]
    ) => (
      <div className="c-agent-settings__choices" data-setting={key}>
        {options.map((o) => {
          const on = draft[agent][key] === o;
          return (
            <label
              key={o}
              className={`c-agent-settings__choice${on ? ' is-on' : ''}`}
            >
              <input
                type="radio"
                name={`${agent}-${key}`}
                checked={on}
                disabled={isViewer}
                onChange={() => set(agent, key, o as Draft[A][keyof Draft[A]])}
              />
              {o}
            </label>
          );
        })}
      </div>
    );

    const toggle = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A] & keyof typeof SETTING_LABEL,
      help?: string
    ) => (
      <label className="c-agent-settings__switch" data-setting={key}>
        <span>
          {SETTING_LABEL[key]}
          {help && <small>{help}</small>}
        </span>
        <input
          type="checkbox"
          checked={!!draft[agent][key]}
          disabled={isViewer}
          onChange={(e) =>
            set(agent, key, e.target.checked as Draft[A][keyof Draft[A]])
          }
        />
      </label>
    );

    const setIdentity = (patch: Partial<AgentIdentity>) =>
      setDraft((d) => ({ ...d, identity: { ...d.identity, ...patch } }));
    const textField = (
      key: keyof AgentIdentity,
      label: string,
      lines?: number
    ) => (
      <label className="c-agent-settings__field" data-setting={key}>
        <span className="c-agent-settings__label">{label}</span>
        {lines ? (
          <textarea
            rows={lines}
            value={draft.identity[key] ?? ''}
            disabled={isViewer}
            onChange={(e) => setIdentity({ [key]: e.target.value })}
          />
        ) : (
          <input
            type="text"
            value={draft.identity[key] ?? ''}
            disabled={isViewer}
            onChange={(e) => setIdentity({ [key]: e.target.value })}
          />
        )}
      </label>
    );

    const supervisor = (
      <>
        <Section title="Brand">
          {textField('brandName', 'Brand name')}
          {textField('assistantName', 'Assistant name')}
          {textField('description', 'What the brand is', 3)}
        </Section>
        <Section title="How it routes">
          {routes.length === 0 ? (
            <p className="c-agent-settings__none">
              No use-case agents yet — everything goes to the Knowledge Agent.
            </p>
          ) : (
            routes.slice(0, 6).map((r) => (
              <div key={r.id} className="c-agent-settings__route">
                <b>{r.title}</b>
                <span>
                  {r.intents.length
                    ? `Intents: ${r.intents.slice(0, 4).join(', ')}`
                    : 'Reached from another agent’s flow'}
                </span>
              </div>
            ))
          )}
          {routes.length > 6 && (
            <p className="c-agent-settings__none">
              and {routes.length - 6} more use-case agents on the canvas
            </p>
          )}
          <div className="c-agent-settings__route">
            <b>Knowledge Agent</b>
            <span>Everything else it can answer from its sources</span>
          </div>
        </Section>
        <Section title={SETTING_LABEL.clarify}>
          {choice('supervisor', 'clarify', SUPERVISOR_CHOICES.clarify)}
        </Section>
        <Section title="When it hands off">
          {toggle('supervisor', 'handoffAsk')}
          <p className="c-agent-settings__label">
            {SETTING_LABEL.handoffUpset}
          </p>
          {choice(
            'supervisor',
            'handoffUpset',
            SUPERVISOR_CHOICES.handoffUpset
          )}
          <label
            className="c-agent-settings__switch"
            data-setting="handoffFails"
          >
            <span>{SETTING_LABEL.handoffFails}</span>
            <select
              value={draft.supervisor.handoffFails}
              disabled={isViewer}
              onChange={(e) =>
                set(
                  'supervisor',
                  'handoffFails',
                  e.target.value as SupervisorSettings['handoffFails']
                )
              }
            >
              {SUPERVISOR_CHOICES.handoffFails.map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
        </Section>
        <Section title={SETTING_LABEL.outOfScope}>
          {choice('supervisor', 'outOfScope', SUPERVISOR_CHOICES.outOfScope)}
        </Section>
        <Section title="Small talk">
          {toggle(
            'supervisor',
            'chitchat',
            'Replies briefly to greetings and thanks, then steers back'
          )}
        </Section>
      </>
    );

    const knowledge = (
      <>
        {draft.identity.tone !== undefined && (
          <Section title="Voice">
            {textField('tone', 'Tone')}
            {textField('instructions', 'Global instructions', 5)}
          </Section>
        )}
        <Section title="Sources">
          {settings.files.length === 0 ? (
            <p className="c-agent-settings__none">
              No documents yet — add them under Agent settings.
            </p>
          ) : (
            settings.files.map((file) => (
              <div
                key={file.id ?? file.originalName}
                className="c-agent-settings__route"
              >
                <b>{file.originalName}</b>
                <span>{file.mimeType}</span>
              </div>
            ))
          )}
        </Section>
        <Section title="How it answers">
          <p className="c-agent-settings__label">{SETTING_LABEL.length}</p>
          {choice('knowledge', 'length', KNOWLEDGE_CHOICES.length)}
          {toggle('knowledge', 'cite')}
          {toggle('knowledge', 'strict')}
          {toggle('knowledge', 'disclaimer')}
        </Section>
        <Section title={SETTING_LABEL.noAnswer}>
          {choice('knowledge', 'noAnswer', KNOWLEDGE_CHOICES.noAnswer)}
        </Section>
      </>
    );

    return (
      <aside
        className={`c-agent-settings${embedded ? ' c-agent-settings--embedded' : ''}`}
        aria-label="Agent settings"
        data-testid="bobcat-agent-settings"
      >
        {!embedded && (
          <div className="c-agent-settings__head">
            <div>
              <TextTitle size="xsmall">Agent settings</TextTitle>
            </div>
            <IconButton
              name="close"
              size="small"
              variant="tertiary"
              aria-label="Close"
              onClick={() => agentSettingsStore.close()}
            />
          </div>
        )}

        <div className="c-agent-settings__tabs" role="tablist">
          {(['supervisor', 'knowledge'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`c-agent-settings__tab${tab === t ? ' is-on' : ''}`}
              onClick={() => agentSettingsStore.setTab(t)}
            >
              {AGENT_TITLE[t]}
            </button>
          ))}
        </div>

        <div className="c-agent-settings__body" ref={body}>
          {!settings.loaded ? (
            <p className="c-agent-settings__none">Loading the solution…</p>
          ) : (
            <Fragment key={tab}>
              {aiSteps[tab].length > 0 && (
                <Section title="Model">
                  <AgentModelSection
                    agent={tab}
                    steps={aiSteps[tab]}
                    routing={tab === 'supervisor' ? routing : null}
                    models={draft.models}
                    decisions={draft.decisions}
                    isViewer={isViewer}
                    onModel={(rowId, model) => {
                      setModelSaved(false);
                      setDraft((d) => ({
                        ...d,
                        models: { ...d.models, [rowId]: model },
                      }));
                    }}
                    onDecisions={(on) => {
                      setModelSaved(false);
                      setDraft((d) => ({ ...d, decisions: on }));
                    }}
                    footer={
                      modelSaved && (
                        <ModelSaved
                          draftVersion={draftVersion}
                          onDeploy={() => tableStore.setActiveStage('deploy')}
                        />
                      )
                    }
                  />
                </Section>
              )}
              {tab === 'supervisor' ? supervisor : knowledge}
              {tab === 'supervisor' ? (
                <Section title="Tasks and example phrases">
                  {extras.supervisor}
                </Section>
              ) : (
                <Section title="Documents">{extras.knowledge}</Section>
              )}
              <Section title="Prompt">
                {draft.prompts[tab] === null ? (
                  <p className="c-agent-settings__none">
                    This solution has no {AGENT_TITLE[tab]} model step with a
                    prompt to edit.
                  </p>
                ) : (
                  <AgentPromptEditor
                    title={`${AGENT_TITLE[tab]} prompt`}
                    description="The settings choices are added to the end of it when you save."
                    value={draft.prompts[tab] ?? ''}
                    isViewer={isViewer}
                    testId={`bobcat-agent-settings-prompt-${tab}`}
                    onChange={(text) =>
                      setDraft((d) => ({
                        ...d,
                        prompts: { ...d.prompts, [tab]: text },
                      }))
                    }
                  />
                )}
                <LangfusePromptCards
                  agent={tab}
                  isViewer={isViewer}
                  busy={dirty}
                />
              </Section>
              {aiSteps[tab].length === 0 && (
                <Section title="Model">
                  <p className="c-agent-settings__none">
                    {settings.model}
                    {settings.temperature !== undefined &&
                      settings.temperature !== null &&
                      ` · temperature ${settings.temperature}`}{' '}
                    ·{' '}
                    {modelNode(tab) && (
                      <button
                        type="button"
                        className="c-agent-settings__link"
                        onClick={() => {
                          const node = modelNode(tab);
                          if (node) tableStore.openNodeOnCanvas(node.id);
                        }}
                      >
                        Edit on the canvas (#{modelNode(tab)?.nodeNumber})
                      </button>
                    )}
                  </p>
                </Section>
              )}
            </Fragment>
          )}
        </div>

        {!isViewer && (
          <div className="c-agent-settings__foot">
            <Button
              size="small"
              variant="ghost"
              disabled={!dirty || saving}
              onClick={() => {
                setDraft(JSON.parse(loadedKey) as Draft);
                extras.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              disabled={!dirty || saving}
              test-id="bobcat-agent-settings-save"
              onClick={() => void saveAll()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </aside>
    );
  }
);
